import React, { useEffect, useState } from "react";
import {
  GiftOutlined,
  InfoCircleOutlined,
  QuestionCircleOutlined,
  LockOutlined,
} from "@ant-design/icons";
import {
  Row,
  Col,
  Progress,
  Typography,
  Spin,
  Image,
  Button,
  Tooltip,
} from "antd";

import { useAppDispatch, useAppSelector } from "../../../stores";
import {
  getDivisionTiersAction,
  getDivisionTiersMeAction,
  getListDivisionTiersRewardsAction,
  nftRewardAction,
} from "../../../stores/screens/divisions/divisions.action";
import {
  EventIcon,
  MCCIcon,
  SpentIcon,
} from "../../../resources/svgs/division";

import {
  DivisionTabStyled,
  ModalRulesStyled,
  ModalSuccessStyled,
} from "./style";
import RulesDivisions from "./RulesModal";
import MCCIcon2 from "../../../resources/svgs/division/MccIcon2";
import PolygonIcon from "../../../resources/svgs/division/Polygon";
import { formatNumber } from "../../../utils/common";

const { Title, Text } = Typography;

const DivisionTab = ({ activeTab }) => {
  const dispatch = useAppDispatch();
  const { divisionMe, isLoading, divisionTiers } = useAppSelector(
    (state) => state.divisions
  );

  const currentTier = divisionMe?.tier ?? 0;
  const nextTier = divisionMe?.next_tier ?? currentTier + 1;

  const [ruleModal, setRuleModal] = useState({
    open: false,
    data: null,
  });
  const [success, setSuccess] = useState({
    open: false,
    info: null,
  });

  useEffect(() => {
    if (activeTab === "DIVISIONS") {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchData = () => {
    dispatch(getDivisionTiersMeAction({}));
    dispatch(getDivisionTiersAction({ page: 1, limit: 100 }));
  };

  if (isLoading || !divisionMe) {
    return (
      <div style={{ textAlign: "center", padding: 40 }}>
        <Spin size="large" />
      </div>
    );
  }

  const subs = [
    {
      key: "spent",
      icon: <SpentIcon />,
      current: formatNumber(divisionMe.current_nft_number),
      max: formatNumber(divisionMe.nft_count_condition),
      percentage: divisionMe.nft_count_percentage,
      info: `Own ${formatNumber(
        divisionMe.nft_count_condition
      )} NFTs to qualify`,
    },
    {
      key: "nft",
      icon: <MCCIcon />,
      current: formatNumber(divisionMe.current_spent_point),
      max: formatNumber(divisionMe.spent_point_condition),
      percentage: divisionMe.spent_point_percentage,
      info: `Use ${formatNumber(
        divisionMe.spent_point_condition
      )} MCC Points to qualify`,
    },
    {
      key: "event",
      icon: <EventIcon />,
      current: formatNumber(divisionMe.current_event_number),
      max: formatNumber(divisionMe.event_count_condition),
      percentage: divisionMe.event_percentage,
      info: `Join ${formatNumber(
        divisionMe.event_count_condition
      )} event to qualify`,
    },
  ];

  const getTierStatus = (tier) => {
    if (tier.is_lock) {
      return {
        status: "LOCKED",
        statusColor: "#61637A",
        statusBackground: "#2e2f55",
        showLock: true,
        color: "#61637a",
      };
    } else if (!tier.is_lock && tier.status === "INIT") {
      return {
        status: "CLAIM",
        statusColor: "white",
        statusBackground: "#156DF2",
        showLock: false,
        color: "#fff",
      };
    } else if (
      (!tier.is_lock && tier.status === "SUCCESS") ||
      (!tier.is_lock && !tier.nft_reward_id)
    ) {
      return {
        status: "COMPLETE!",
        statusColor: "#61D34A",
        statusBackground: "#0C0D22",
        showLock: false,
        color: "#fff",
      };
    } else if (!tier.is_lock && tier.status === "WAIT_CONFIRM") {
      return {
        status: "WAIT CONFIRM!",
        statusColor: "#F2C94C",
        statusBackground: "#0C0D22",
        showLock: false,
        color: "#fff",
      };
    }
    return {
      status: "",
      statusColor: "",
      statusBackground: "",
      showLock: false,
      color: "#61637a",
    };
  };

  function computeProgressHeight(tiers, currentTier) {
    const n = tiers.length;
    if (n === 0) return "0%";

    if (currentTier <= tiers[0]) return "0%";
    if (currentTier >= tiers[n - 1]) return "100%";

    for (let i = 0; i < n - 1; i++) {
      const a = tiers[i],
        b = tiers[i + 1];
      if (currentTier >= a && currentTier <= b) {
        const local = (currentTier - a) / (b - a);
        const global = (i + local) / (n - 1);
        return `${global * 100}%`;
      }
    }

    return "0%";
  }

  const sortedTiers =
    Array.isArray(divisionTiers) && divisionTiers.length > 0
      ? [...divisionTiers].sort((a, b) => a.tier - b.tier)
      : [];

  const tierValues = sortedTiers.map((t) => t.tier);
  const progressHeight = computeProgressHeight(tierValues, currentTier);

  const handleOpenClaimSuccess = (tier) => {
    setSuccess({
      open: true,
      info: tier,
    });
    fetchData();
  };

  const handleClaimTier = (tier) => {
    dispatch(
      nftRewardAction({
        divisionTierNftRewardId: tier.id,
        callback: () => {
          handleOpenClaimSuccess(tier);
        },
      })
    );
  };

  const handleFetchDataRules = async () => {
    const dataPointBonus = await dispatch(
      getListDivisionTiersRewardsAction({
        page: 1,
        limit: 100,
        type: "POINT_BONUS",
      })
    );
    const dataRebateRate = await dispatch(
      getListDivisionTiersRewardsAction({
        page: 1,
        limit: 100,
        type: "REBATE_RATE",
      })
    );
    const dataNftReward = await dispatch(
      getListDivisionTiersRewardsAction({
        page: 1,
        limit: 100,
        type: "NFT_REWARD",
      })
    );

    const data = {
      pointBonus: dataPointBonus?.payload?.data,
      rebateRate: dataRebateRate?.payload?.data,
      nftReward: dataNftReward?.payload?.data,
    };

    setRuleModal({
      open: true,
      data,
    });
  };

  return (
    <DivisionTabStyled>
      <div className="content-1">
        <Row justify="center">
          <Title level={3} className="title">
            TIER{" "}
            <Title
              style={{
                marginBottom: 10,
              }}
              level={1}
            >
              {currentTier}
            </Title>
            {divisionMe?.include_nft_reward && (
              <GiftOutlined style={{ fontSize: 24, color: "#156DF2" }} />
            )}
          </Title>
        </Row>
        {divisionMe?.modified && (
          <Title level={3} className="modified">
            Division updated with new changes!
          </Title>
        )}

        <div className="main-progress">
          <Title level={4}>TIER {currentTier}</Title>
          <Progress
            percent={divisionMe?.total_percentage ?? 0}
            showInfo={true}
            strokeWidth={33}
            percentPosition={{ align: "center", type: "inner", offset: 0 }}
            strokeLinecap="round"
            trailColor="#222336"
            format={(percent) => (
              <span
                style={{
                  color: "white",
                  fontSize: "17px",
                  fontWeight: 700,
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 1,
                }}
              >
                {percent}%
              </span>
            )}
            strokeColor={{
              "0%": "#156DF2",
              "100%": "#9747FF",
            }}
          />
          <Title level={4}>TIER {nextTier}</Title>
        </div>

        <Row gutter={50} justify="center" className="sub-items">
          {subs.map(({ key, icon, current, max, percentage, info }) => {
            if (!max || max === 0 || max === "0") return null;
            return (
              <Col span={8} key={key}>
                <div className="sub-item">
                  <div className="icon">{icon}</div>
                  <div className="progress">
                    <div className="label">
                      {current} / {max}{" "}
                      <Tooltip title={info} color="#0C0D22">
                        <InfoCircleOutlined style={{ cursor: "pointer" }} />
                      </Tooltip>
                    </div>
                    <Progress
                      percent={percentage}
                      showInfo={false}
                      strokeWidth={16}
                      strokeLinecap="round"
                      trailColor="#222336"
                      strokeColor={{
                        "0%": "#156DF2",
                        "100%": "#9747FF",
                      }}
                    />
                  </div>
                </div>
              </Col>
            );
          })}
        </Row>
      </div>

      <div className="content-2">
        <Row justify={"start"}>
          <Title level={5} className="more-info" onClick={handleFetchDataRules}>
            <QuestionCircleOutlined /> More info
          </Title>
        </Row>
        <Row className="content-table">
          <Col span={2}>
            <Row style={{ height: "100%" }}>
              <Col
                style={{
                  justifyContent: "space-between",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {sortedTiers.map((tier, index) => (
                  <React.Fragment key={index}>
                    <Row
                      justify="center"
                      style={{
                        width: 43,
                        height: 17.42,
                        color: currentTier >= tier.tier ? "white" : "#2d2f55",
                        fontWeight: "bold",
                        fontSize: "1.25rem",
                      }}
                    >
                      {tier.nft_reward_id ? (
                        <GiftOutlined
                          style={{ fontSize: 24, color: "#156DF2" }}
                        />
                      ) : (
                        tier.tier
                      )}
                    </Row>
                  </React.Fragment>
                ))}
              </Col>
              <Col
                style={{
                  width: 20,
                  backgroundColor: "#2d2f55",
                  borderRadius: 20,
                  border: "1px solid #ffffff17",
                }}
              >
                <div
                  style={{
                    height: progressHeight,
                    borderRadius: "20px 20px 0 0",
                    border: "1px solid rgba(255, 255, 255, 0.09)",
                    boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
                    background:
                      "linear-gradient(180deg, #156DF2 0.11%, #9747FF 97.59%, #F3FF4C 199.05%), linear-gradient(180deg, #15DCF2 0%, #64FF45 50.13%, #F3FF4C 102.3%)",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: progressHeight,
                      marginTop: -8,
                    }}
                  >
                    <PolygonIcon />
                  </div>
                </div>
              </Col>
              <div
                style={{
                  color: "white",
                  fontWeight: "bold",
                  marginLeft: 28,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "end",
                    marginTop: 12,
                    height: progressHeight,
                    fontSize: "20px",
                  }}
                >
                  {currentTier}
                </div>
              </div>
            </Row>
          </Col>
          <Col
            span={22}
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              gap: 20,
            }}
          >
            {sortedTiers.length > 0 ? (
              sortedTiers.map((tier, index) => {
                const {
                  status,
                  statusColor,
                  showLock,
                  statusBackground,
                  color,
                } = getTierStatus(tier);
                const point_bonus_reward_text = tier.point_bonus_reward ? (
                  <>
                    {formatNumber(tier.point_bonus_reward)}% point bonus <br />{" "}
                    return rate
                  </>
                ) : null;
                const rebate_bonus_reward_text = tier.rebate_bonus_reward ? (
                  <>
                    {formatNumber(tier.rebate_bonus_reward)}% rebate rate <br />{" "}
                    return rate
                  </>
                ) : null;

                return (
                  <Row key={index} className="tier-row">
                    <Col
                      className={`tier-col ${
                        showLock
                          ? `lock ${tier.nft_reward_id ? "nft" : ""}`
                          : status === "CLAIM"
                          ? "claim"
                          : status === "WAIT_CONFIRM"
                          ? "wait-confirm"
                          : ""
                      }`}
                    >
                      <Col className="tier-name">{tier.tier}</Col>

                      {tier.nft_reward_id && (
                        <Col
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                            minWidth: "250px",
                          }}
                        >
                          <Image
                            src={tier.nft_reward_url}
                            alt="NFT Reward"
                            width={64}
                            preview={false}
                          />
                          <Col
                            style={{
                              color: color,
                              fontSize: "16px",
                              maxWidth: "220px",
                              display: "-webkit-box",
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              lineHeight: "1.2"
                            }}
                          >
                            {tier.nft_reward_name}
                          </Col>
                        </Col>
                      )}

                      {point_bonus_reward_text &&
                        formatNumber(tier.point_bonus_reward) !== "0" && (
                          <Col
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "5px",
                              minWidth: "250px",
                            }}
                          >
                            <MCCIcon2 />
                            <Col
                              style={{
                                color: color,
                                fontSize: "16px",
                              }}
                            >
                              {point_bonus_reward_text}
                            </Col>
                          </Col>
                        )}

                      {rebate_bonus_reward_text &&
                        formatNumber(tier.rebate_bonus_reward) !== "0" && (
                          <Col
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "5px",
                              minWidth: "250px",
                            }}
                          >
                            <MCCIcon2 />
                            <Col
                              style={{
                                color: color,
                                fontSize: "16px",
                              }}
                            >
                              {rebate_bonus_reward_text}
                            </Col>
                          </Col>
                        )}

                      {status && (
                        <Col
                          onClick={
                            status === "CLAIM"
                              ? () => handleClaimTier(tier)
                              : null
                          }
                          className="tier-status"
                          style={{
                            background: statusBackground,
                            color: statusColor,
                            cursor:
                              status === "CLAIM"
                                ? "pointer"
                                : showLock
                                ? "not-allowed"
                                : "initial",
                          }}
                        >
                          {showLock ? (
                            <LockOutlined
                              style={{ fontSize: 24, color: statusColor }}
                            />
                          ) : (
                            status
                          )}
                        </Col>
                      )}
                    </Col>
                  </Row>
                );
              })
            ) : (
              <div style={{ textAlign: "center", padding: 40 }}>
                <Spin size="small" /> Loading tier data...
              </div>
            )}
          </Col>
        </Row>
      </div>

      <ModalSuccessStyled
        centered
        open={success.open}
        footer={null}
        onCancel={() =>
          setSuccess({
            open: false,
            info: null,
          })
        }
      >
        <div>
          <Row justify="center" gutter={32}>
            {success.info?.point_bonus_reward !== null && 
             formatNumber(success.info?.point_bonus_reward) !== "0" && (
              <Col className="item-col" style={{ marginTop: 42 }}>
                <MCCIcon2 />
                <Text
                  style={{
                    color: "#fff",
                    textAlign: "center",
                  }}
                >
                  {formatNumber(success.info?.point_bonus_reward)}% bonus point
                  <br />
                  return rate
                </Text>
              </Col>
            )}
            <Col className="item-col">
              <Image
                src={success.info?.nft_reward_url}
                alt={success.info?.nft_reward_name || "NFT Reward"}
                width={126}
                preview={false}
              />
              <Text
                style={{
                  color: "#fff",
                  textAlign: "center",
                }}
              >
                {success.info?.nft_reward_name}
              </Text>
            </Col>
            {success.info?.rebate_bonus_reward !== null && 
             formatNumber(success.info?.rebate_bonus_reward) !== "0" && (
              <Col className="item-col" style={{ marginTop: 42 }}>
                <MCCIcon2 />
                <Text
                  style={{
                    color: "#fff",
                    textAlign: "center",
                  }}
                >
                  {formatNumber(success.info?.rebate_bonus_reward)}% rebate rate
                  <br />
                  return rate
                </Text>
              </Col>
            )}
          </Row>

          <div style={{ paddingTop: "30px", width: "340px", margin: "0 auto" }}>
            <Text
              style={{
                background:
                  "linear-gradient(90deg, rgba(21,220,242,1) 0%, rgba(100,255,69,1) 49%, rgba(243,255,76,1) 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontFamily: "'Afacad-Bold', Helvetica",
                fontSize: "32px",
                fontWeight: "bold",
              }}
            >
              CONGRATULATIONS!
            </Text>

            <Button
              type="primary"
              onClick={() =>
                setSuccess({
                  open: false,
                  info: null,
                })
              }
              style={{
                marginTop: "11px",
                width: "100%",
                height: "48px",
                borderRadius: "49px",
                background:
                  "linear-gradient(90deg, rgba(21,220,242,1) 0%, rgba(100,255,69,1) 49%, rgba(243,255,76,1) 100%)",
                boxShadow: "0px 4px 4px #00000040",
                fontFamily: "'Afacad-Bold', Helvetica",
                fontSize: "20px",
                color: "#0b0c22",
              }}
            >
              CLOSE
            </Button>

            <div
              style={{
                marginTop: "5px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Text
                style={{
                  color: "white",
                  fontFamily: "'Afacad-Regular', Helvetica",
                  fontSize: "14px",
                }}
              >
                You have received the
              </Text>
              <Text
                style={{
                  color: "white",
                  fontFamily: "'Afacad-Bold', Helvetica",
                  fontSize: "14px",
                  marginLeft: "5px",
                }}
              >
                NFT DEBUT trophy!
              </Text>
            </div>
          </div>
        </div>
      </ModalSuccessStyled>

      <ModalRulesStyled
        centered
        open={ruleModal.open}
        footer={null}
        onCancel={() =>
          setRuleModal({
            open: false,
            data: null,
          })
        }
        width="50%"
      >
        <RulesDivisions
          data={ruleModal?.data}
          onClose={() =>
            setRuleModal({
              open: false,
              data: null,
            })
          }
        />
      </ModalRulesStyled>
    </DivisionTabStyled>
  );
};

export default DivisionTab;
